/**
 * Convert REST API
 *
 * Request quotes and execute cryptocurrency conversions via the Convert REST API.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { ConfigurationRestAPI, RestApiResponse, sendRequest } from '@binance/common';
import { MarketDataApi } from './modules/market-data-api';
import { TradeApi } from './modules/trade-api';

import type {
    ListAllConvertPairsRequest,
    QueryOrderQuantityPrecisionPerAssetRequest,
} from './modules/market-data-api';
import type {
    AcceptQuoteRequest,
    CancelLimitOrderRequest,
    GetConvertTradeHistoryRequest,
    OrderStatusRequest,
    PlaceLimitOrderRequest,
    QueryLimitOpenOrdersRequest,
    SendQuoteRequestRequest,
} from './modules/trade-api';

import type {
    ListAllConvertPairsResponse,
    QueryOrderQuantityPrecisionPerAssetResponse,
} from './types';
import type {
    AcceptQuoteResponse,
    CancelLimitOrderResponse,
    GetConvertTradeHistoryResponse,
    OrderStatusResponse,
    PlaceLimitOrderResponse,
    QueryLimitOpenOrdersResponse,
    SendQuoteRequestResponse,
} from './types';

export class RestAPI {
    private configuration: ConfigurationRestAPI;
    private marketDataApi: MarketDataApi;
    private tradeApi: TradeApi;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.marketDataApi = new MarketDataApi(configuration);
        this.tradeApi = new TradeApi(configuration);
    }

    /**
     * Generic function to send a request.
     * @param endpoint - The API endpoint to call.
     * @param method - HTTP method to use (GET, POST, DELETE, etc.).
     * @param queryParams - Query parameters for the request.
     * @param bodyParams - Body parameters for the request.
     *
     * @returns A promise resolving to the response data object.
     */
    sendRequest<T>(
        endpoint: string,
        method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH',
        queryParams: Record<string, unknown> = {},
        bodyParams: Record<string, unknown> = {}
    ): Promise<RestApiResponse<T>> {
        return sendRequest<T>(
            this.configuration,
            endpoint,
            method,
            queryParams,
            bodyParams,
            undefined,
            undefined
        );
    }

    /**
     * Generic function to send a signed request.
     * @param endpoint - The API endpoint to call.
     * @param method - HTTP method to use (GET, POST, DELETE, etc.).
     * @param queryParams - Query parameters for the request.
     * @param bodyParams - Body parameters for the request.
     *
     * @returns A promise resolving to the response data object.
     */
    sendSignedRequest<T>(
        endpoint: string,
        method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH',
        queryParams: Record<string, unknown> = {},
        bodyParams: Record<string, unknown> = {}
    ): Promise<RestApiResponse<T>> {
        return sendRequest<T>(
            this.configuration,
            endpoint,
            method,
            queryParams,
            bodyParams,
            undefined,
            undefined,
            { isSigned: true }
        );
    }

    /**
     * Query for all convertible token pairs and the tokens’ respective
     * upper/lower limits
     *
     * Weight(IP): 3000
     *
     * Notes:
     * - User needs to supply either or both input parameters.
     * - If only one of `fromAsset` and `toAsset` is provided, only partial token pairs are returned.
     *
     * @summary List All Convert Pairs
     * @param {ListAllConvertPairsRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<ListAllConvertPairsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-convert/api/rest-api/market-data#list-all-convert-pairs Binance API Documentation}
     */
    listAllConvertPairs(
        requestParameters: ListAllConvertPairsRequest = {}
    ): Promise<RestApiResponse<ListAllConvertPairsResponse>> {
        return this.marketDataApi.listAllConvertPairs(requestParameters);
    }

    /**
     * Query for supported asset’s precision information
     *
     * Weight(IP): 100
     *
     * Security Type: USER_DATA
     *
     * @summary Query order quantity precision per asset (USER_DATA)
     * @param {QueryOrderQuantityPrecisionPerAssetRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<QueryOrderQuantityPrecisionPerAssetResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-convert/api/rest-api/market-data#query-order-quantity-precision-per-asset Binance API Documentation}
     */
    queryOrderQuantityPrecisionPerAsset(
        requestParameters: QueryOrderQuantityPrecisionPerAssetRequest = {}
    ): Promise<RestApiResponse<QueryOrderQuantityPrecisionPerAssetResponse>> {
        return this.marketDataApi.queryOrderQuantityPrecisionPerAsset(requestParameters);
    }

    /**
     * Accept the offered quote by quote ID.
     *
     * Weight(UID): 500
     *
     * Security Type: TRADE
     *
     * @summary Accept Quote (TRADE)
     * @param {AcceptQuoteRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<AcceptQuoteResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-convert/api/rest-api/trade#accept-quote Binance API Documentation}
     */
    acceptQuote(
        requestParameters: AcceptQuoteRequest
    ): Promise<RestApiResponse<AcceptQuoteResponse>> {
        return this.tradeApi.acceptQuote(requestParameters);
    }

    /**
     * Enable users to cancel a limit order
     *
     * Weight(UID): 200
     *
     * Security Type: TRADE
     *
     * @summary Cancel limit order (TRADE)
     * @param {CancelLimitOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<CancelLimitOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-convert/api/rest-api/trade#cancel-limit-order Binance API Documentation}
     */
    cancelLimitOrder(
        requestParameters: CancelLimitOrderRequest
    ): Promise<RestApiResponse<CancelLimitOrderResponse>> {
        return this.tradeApi.cancelLimitOrder(requestParameters);
    }

    /**
     * Get Convert Trade History
     *
     * Weight(UID): 3000
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * - The max interval between `startTime` and `endTime` is 30 days.
     *
     * @summary Get Convert Trade History (USER_DATA)
     * @param {GetConvertTradeHistoryRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetConvertTradeHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-convert/api/rest-api/trade#get-convert-trade-history Binance API Documentation}
     */
    getConvertTradeHistory(
        requestParameters: GetConvertTradeHistoryRequest
    ): Promise<RestApiResponse<GetConvertTradeHistoryResponse>> {
        return this.tradeApi.getConvertTradeHistory(requestParameters);
    }

    /**
     * Query order status by order ID.
     *
     * Weight(UID): 100
     *
     * Security Type: USER_DATA
     *
     * @summary Order status (USER_DATA)
     * @param {OrderStatusRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OrderStatusResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-convert/api/rest-api/trade#order-status Binance API Documentation}
     */
    orderStatus(
        requestParameters: OrderStatusRequest = {}
    ): Promise<RestApiResponse<OrderStatusResponse>> {
        return this.tradeApi.orderStatus(requestParameters);
    }

    /**
     * Enable users to place a limit order
     *
     * Weight(UID): 500
     *
     * Security Type: TRADE
     *
     * Notes:
     * - `baseAsset` and `quoteAsset` can be determined via the `exchangeInfo` endpoint.
     * - Limit price is defined from `baseAsset` to `quoteAsset`.
     * - Exactly one of `baseAmount` or `quoteAmount` should be sent.
     *
     * @summary Place limit order (TRADE)
     * @param {PlaceLimitOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<PlaceLimitOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-convert/api/rest-api/trade#place-limit-order Binance API Documentation}
     */
    placeLimitOrder(
        requestParameters: PlaceLimitOrderRequest
    ): Promise<RestApiResponse<PlaceLimitOrderResponse>> {
        return this.tradeApi.placeLimitOrder(requestParameters);
    }

    /**
     * Query current open limit orders
     *
     * Weight(UID): 3000
     *
     * Security Type: USER_DATA
     *
     * @summary Query limit open orders (USER_DATA)
     * @param {QueryLimitOpenOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<QueryLimitOpenOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-convert/api/rest-api/trade#query-limit-open-orders Binance API Documentation}
     */
    queryLimitOpenOrders(
        requestParameters: QueryLimitOpenOrdersRequest = {}
    ): Promise<RestApiResponse<QueryLimitOpenOrdersResponse>> {
        return this.tradeApi.queryLimitOpenOrders(requestParameters);
    }

    /**
     * Request a quote for the requested token pairs
     *
     * Weight(UID): 200
     *
     * Security Type: TRADE
     *
     * Notes:
     * - Either `fromAmount` or `toAmount` should be sent.
     * - `quoteId` is returned only if you have enough funds to convert.
     *
     * @summary Send Quote Request (TRADE)
     * @param {SendQuoteRequestRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<SendQuoteRequestResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-convert/api/rest-api/trade#send-quote-request Binance API Documentation}
     */
    sendQuoteRequest(
        requestParameters: SendQuoteRequestRequest
    ): Promise<RestApiResponse<SendQuoteRequestResponse>> {
        return this.tradeApi.sendQuoteRequest(requestParameters);
    }
}
